// Components
import { Checkbox, Stack } from "@mantine/core";
import { FormField } from "@benchmark/ui/components/atomic";
import { FormGroup } from "@benchmark/ui/components/atomic";
import { FormSection } from "@benchmark/ui/components/atomic";
import { TrimmedTextInput as TextInput } from "@benchmark/ui/components/atomic";
import { InsuranceFieldsGroup } from "./InsuranceFieldsGroup";

// Hooks
import { useCallback } from "react";

// Types
import type { UseFormReturnType } from "@mantine/form";
import type { PatientFormData } from "@benchmark/core/types";

/**
 * Props for InsuranceSection component.
 * @agentic @benchmark/core
 */
type InsuranceSectionProps = {
  form: UseFormReturnType<PatientFormData>;
  title?: string;
  subtitle?: string;
  collapsible?: boolean;
  defaultCollapsed?: boolean;
};

/**
 * Form section for insurance (self-pay, primary, secondary).
 * @agentic @benchmark/core
 */
export const InsuranceSection = ({
  form,
  title = "Insurance Information",
  subtitle,
  collapsible = false,
  defaultCollapsed: propDefaultCollapsed,
}: InsuranceSectionProps) => {
  // Calculate defaultCollapsed based on whether insurance data exists
  const hasPrimaryInsurance =
    !form.values["self-pay"] &&
    (!!form.values["primary-insurance-provider"] ||
      !!form.values["primary-subscriber-id"] ||
      !!form.values["primary-relationship-to-subscriber"]);
  const hasSecondaryInsurance =
    form.values["has-secondary-insurance"] === true &&
    (!!form.values["secondary-insurance-provider"] ||
      !!form.values["secondary-subscriber-id"] ||
      !!form.values["secondary-relationship-to-subscriber"]);

  const defaultCollapsed =
    propDefaultCollapsed !== undefined
      ? propDefaultCollapsed
      : !hasPrimaryInsurance && !hasSecondaryInsurance;

  const selfPayValue = form.values["self-pay"] as boolean | undefined;

  const extractedInsuranceProvider = form.values[
    "_extracted-insurance-provider"
  ] as string | undefined;
  const extractedSubscriberId = form.values["_extracted-subscriber-id"] as
    string | undefined;

  // Clear insurance fields when self-pay is enabled
  // This ensures a clean slate when the user unchecks self-pay
  const handleSelfPayChange = useCallback(
    (checked: boolean) => {
      form.setFieldValue("self-pay", checked);

      if (checked) {
        // Clear primary insurance fields
        form.setFieldValue("primary-insurance-provider", "");
        form.setFieldValue("primary-insurance-provider-display", undefined);
        form.setFieldValue("primary-insurance-plan", "");
        form.setFieldValue("primary-insurance-plan-display", undefined);
        form.setFieldValue("primary-subscriber-id", "");
        form.setFieldValue("primary-relationship-to-subscriber", "self");
        form.setFieldValue("primary-subscriber-first-name", "");
        form.setFieldValue("primary-subscriber-last-name", "");
        form.setFieldValue("primary-subscriber-dob", "");
        form.setFieldValue("primary-subscriber-phone", "");

        // Clear secondary insurance fields
        form.setFieldValue("has-secondary-insurance", false);
        form.setFieldValue("secondary-insurance-provider", "");
        form.setFieldValue("secondary-insurance-provider-display", undefined);
        form.setFieldValue("secondary-insurance-plan", "");
        form.setFieldValue("secondary-insurance-plan-display", undefined);
        form.setFieldValue("secondary-subscriber-id", "");
        form.setFieldValue("secondary-relationship-to-subscriber", "self");
        form.setFieldValue("secondary-subscriber-first-name", "");
        form.setFieldValue("secondary-subscriber-last-name", "");
        form.setFieldValue("secondary-subscriber-dob", "");
        form.setFieldValue("secondary-subscriber-phone", "");
      }
    },
    [form],
  );

  return (
    <FormSection
      title={title}
      subtitle={subtitle}
      collapsible={collapsible}
      defaultCollapsed={defaultCollapsed}
    >
      <Stack gap="md">
        {(extractedInsuranceProvider || extractedSubscriberId) && (
          <FormGroup>
            {extractedInsuranceProvider && (
              <FormField label="Insurance Provider (extracted from fax)">
                <TextInput
                  value={extractedInsuranceProvider}
                  readOnly
                  variant="filled"
                />
              </FormField>
            )}
            {extractedSubscriberId && (
              <FormField label="Subscriber ID (extracted from fax)">
                <TextInput
                  value={extractedSubscriberId}
                  readOnly
                  variant="filled"
                />
              </FormField>
            )}
          </FormGroup>
        )}

        <FormField>
          <Checkbox
            label="Self-Pay (No Insurance)"
            checked={selfPayValue || false}
            onChange={(e) => handleSelfPayChange(e.currentTarget.checked)}
          />
        </FormField>

        <InsuranceFieldsGroup
          form={
            form as unknown as UseFormReturnType<
              Record<string, string | boolean | undefined>
            >
          }
          isPrimary={true}
        />

        <FormField>
          <Checkbox
            label="Secondary Insurance"
            checked={form.values["has-secondary-insurance"] || false}
            disabled={selfPayValue === true}
            onChange={(e) =>
              form.setFieldValue(
                "has-secondary-insurance",
                e.currentTarget.checked,
              )
            }
          />
        </FormField>

        {form.values["has-secondary-insurance"] && (
          <InsuranceFieldsGroup
            form={
              form as unknown as UseFormReturnType<
                Record<string, string | boolean | undefined>
              >
            }
            isPrimary={false}
          />
        )}
      </Stack>
    </FormSection>
  );
};
